import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  ContainedModuleType,
  HomeModuleTileType,
  ModuleAccessEnum,
  PermissionEnum,
} from '@jfw/types';
import { AuthService } from '../../services/auth/auth.service';
import { FeedbackApiService } from '../../services/feedback/feedback-api.service';
import { HomeSearchUiService } from '../../services/home/home-search-ui.service';
import { HomeTab, HomeTabsComponent } from './home-tabs/home-tabs.component';

const FAVORITES_TAB_ID = 'favorites';
const FEEDBACK_LIST_ROUTE = '/platform-feedback/list';

// A tile normally renders as-is. A search match against one of a collapsed
// suite tile's contained modules (e.g. "Account Balance" inside "Wholesale
// Order Management") carries that sub-module along so the template can note
// which one matched and route to its own sub-route instead of the suite's
// default entry route.
type HomeTileEntry = {
  tile: HomeModuleTileType;
  matchedContainedModule?: ContainedModuleType;
};

// EXT-5702: the launcher header's text is either the app's own (a
// `platformPortal.home.*` key, translated in the template so it follows a
// language change) or data-driven category text from @jfw/types (shown
// as-is until module/category text is translated in its own ticket).
type HomeText = { key: string; params?: Record<string, string> } | { text: string };

type HomeContent = {
  title: HomeText;
  blurb: HomeText;
  tiles: Array<HomeTileEntry>;
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, RouterModule, HomeTabsComponent, TranslocoDirective],
})
export class HomeComponent {
  private readonly authService = inject(AuthService);
  private readonly homeSearchUiService = inject(HomeSearchUiService);

  protected readonly platformUser = this.authService.platformUser;

  // EXT-6172: the Feedback tile carries the count of New feedback -- fetched
  // once, only for users who can see Feedback. Matched by route, so it still
  // lands on the right tile if Feedback is ever folded into a suite.
  private readonly feedbackApi = inject(FeedbackApiService);
  private readonly canViewFeedback = this.authService.hasPermissions([
    PermissionEnum['PlatformFeedback.View'],
  ]);
  protected readonly newFeedbackCount = signal<number | null>(null);
  private feedbackCountRequested = false;
  private readonly loadFeedbackCount = effect(() => {
    if (this.canViewFeedback() && !this.feedbackCountRequested) {
      this.feedbackCountRequested = true;
      this.feedbackApi.count().subscribe({
        next: (response) => this.newFeedbackCount.set(response.count),
        error: () => this.newFeedbackCount.set(null),
      });
    }
  });

  protected isFeedbackTile(tile: HomeModuleTileType): boolean {
    return (
      tile.routes.includes(FEEDBACK_LIST_ROUTE) ||
      !!tile.containedModules?.some((module) =>
        module.routes.includes(FEEDBACK_LIST_ROUTE),
      )
    );
  }

  protected readonly activeTab = signal<string>(FAVORITES_TAB_ID);
  protected readonly searchQuery = this.homeSearchUiService.query;

  protected readonly moduleTabs = toSignal(this.authService.moduleTabs$, {
    initialValue: [],
  });

  protected readonly homeTabs = computed<Array<HomeTab>>(() => [
    { id: FAVORITES_TAB_ID, labelKey: 'platformPortal.home.favorites' },
    ...this.moduleTabs().map((tab) => ({ id: tab.category, label: tab.label })),
  ]);

  protected readonly favorites = computed(
    () => this.platformUser()?.favorites ?? [],
  );

  private readonly allTiles = computed<Array<HomeModuleTileType>>(() =>
    this.moduleTabs().flatMap((tab) => tab.tiles),
  );

  private readonly favoriteTiles = computed<Array<HomeTileEntry>>(() => {
    let favorites = this.favorites();
    return this.allTiles()
      .filter((tile) => favorites.includes(tile.enumeration))
      .map((tile) => ({ tile }));
  });

  private readonly searchResults = computed<Array<HomeTileEntry>>(() => {
    let query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return [];
    }
    let entries: Array<HomeTileEntry> = [];
    for (let tile of this.allTiles()) {
      let ownMatch = `${tile.name} ${tile.description} ${tile.categoryLabel}`
        .toLowerCase()
        .includes(query);
      if (ownMatch) {
        entries.push({ tile });
        continue;
      }
      let matchedContainedModule = tile.containedModules?.find((containedModule) =>
        `${containedModule.name} ${containedModule.description}`
          .toLowerCase()
          .includes(query),
      );
      if (matchedContainedModule) {
        entries.push({ tile, matchedContainedModule });
      }
    }
    return entries.sort((a, b) => a.tile.name.localeCompare(b.tile.name));
  });

  protected readonly activeContent = computed((): HomeContent => {
    let query = this.searchQuery().trim();
    if (query) {
      return {
        title: { key: 'searchTitle' },
        blurb: { key: 'searchBlurb' },
        tiles: this.searchResults(),
      };
    }
    if (this.activeTab() === FAVORITES_TAB_ID) {
      return {
        title: { key: 'favorites' },
        blurb: { key: 'favoritesBlurb' },
        tiles: this.favoriteTiles(),
      };
    }
    let tab = this.moduleTabs().find(
      (moduleTab) => moduleTab.category === this.activeTab(),
    );
    return {
      title: { text: tab?.label ?? '' },
      blurb: { text: tab?.blurb ?? '' },
      tiles: (tab?.tiles ?? []).map((tile): HomeTileEntry => ({ tile })),
    };
  });

  protected readonly emptyText = computed((): HomeText =>
    this.searchQuery().trim()
      ? { key: 'noSearchMatches', params: { query: this.searchQuery().trim() } }
      : { key: 'noFavorites' },
  );

  /** Narrows a HomeText for the template. */
  protected isKey(
    value: HomeText,
  ): value is { key: string; params?: Record<string, string> } {
    return 'key' in value;
  }

  protected selectTab(id: string): void {
    this.activeTab.set(id);
    this.homeSearchUiService.clear();
  }

  protected isPinned(enumeration: ModuleAccessEnum): boolean {
    return this.favorites().includes(enumeration);
  }

  protected togglePin(enumeration: ModuleAccessEnum, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.authService.toggleFavorite(enumeration);
  }
}
